import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Define interface for API response
interface QuotationStatus {
  quotation_status: "Close Won" | "Close Lost" | "In Process";
  total: number;
}

interface ApiResponse {
  status: number;
  message: string;
  data: {
    quotation_status: QuotationStatus[];
    quotation_percentage: string;
  };
}

export default function Quotation() {
  const [series, setSeries] = useState<number[]>([0]);
  const [quotationData, setQuotationData] = useState<{
    closeWon: number;
    closeLost: number;
    inProcess: number;
  }>({
    closeWon: 0,
    closeLost: 0,
    inProcess: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token) {
        setError("Authorization required. Please log in.");
        navigate("/signin");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dashboard-summary`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        const data: ApiResponse = await response.json();

        if (data.status === 200) {
          const quotationStatus = data.data.quotation_status;
          const percentage = parseFloat(data.data.quotation_percentage);

          setSeries([percentage]);

          const statusMap: { [key in QuotationStatus["quotation_status"]]: number } = {
            "Close Won": 0,
            "Close Lost": 0,
            "In Process": 0,
          };

          quotationStatus.forEach((status: QuotationStatus) => {
            statusMap[status.quotation_status] = status.total;
          });

          setQuotationData({
            closeWon: statusMap["Close Won"],
            closeLost: statusMap["Close Lost"],
            inProcess: statusMap["In Process"],
          });
        } else {
          setError("Failed to fetch data");
        }
      } catch (err: any) {
        setError("Error fetching data: " + (err?.message || "Unknown error"));
      }
    };

    fetchData();
  }, [navigate]);

  const options: ApexOptions = {
    colors: ["#465FFF"],
    chart: {
      fontFamily: "Outfit, sans-serif",
      type: "radialBar",
      height: 200,
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {
      radialBar: {
        startAngle: -95,
        endAngle: 95,
        hollow: {
          size: "80%",
        },
        track: {
          background: "#E4E7EC",
          strokeWidth: "100%",
          margin: 5,
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            fontSize: "24px",
            fontWeight: "600",
            offsetY: -10,
            color: "#1D2939",
            formatter: function (val) {
              return val + "%";
            },
          },
        },
      },
    },
    fill: {
      type: "solid",
      colors: ["#465FFF"],
    },
    stroke: {
      lineCap: "round",
    },
    labels: ["In Process"],
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-100 dark:border-gray-800 dark:bg-white/[0.03]">
      <div className="px-5 pt-5 bg-white shadow-default rounded-2xl pb-9 dark:bg-gray-900 sm:px-6 sm:pt-6">
        <div className="flex justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Quotation Status
            </h3>
            <p className="mt-1 text-gray-500 text-theme-sm dark:text-gray-400">
              Overview of your quotation progress
            </p>
          </div>
          <div className="relative inline-block">
            <button
              onClick={() => navigate("/quotation-list")}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 px-3 py-1 rounded-md text-sm font-medium"
            >
              View More
            </button>
          </div>
        </div>
        <div className="relative">
          <div className="max-h-[200px]">
            <Chart
              options={options}
              series={series}
              type="radialBar"
              height={200}
            />
          </div>
        </div>
        <p className="mx-auto mt-10 w-full max-w-[380px] text-center text-sm text-gray-500 sm:text-base">
          You have {quotationData.inProcess} quotations in process, with a {series[0]}% increase from last week. Keep pushing forward!
        </p>
      </div>
      <div className="flex items-center justify-center gap-5 px-6 py-3.5 sm:gap-8 sm:py-5">
        <div>
          <p
            className="mb-1 text-center text-theme-xs sm:text-sm"
            style={{ color: '#039855' }}
          >
            Close Won
          </p>
          <p
            className="text-center text-base font-semibold sm:text-lg"
            style={{ color: '#039855' }}
          >
            {quotationData.closeWon}
          </p>
        </div>
        <div className="w-px bg-gray-200 h-7 dark:bg-gray-800"></div>
        <div>
          <p
            className="mb-1 text-center text-theme-xs sm:text-sm"
            style={{ color: '#D92D20' }}
          >
            Close Lost
          </p>
          <p
            className="text-center text-base font-semibold sm:text-lg"
            style={{ color: '#D92D20' }}
          >
            {quotationData.closeLost}
          </p>
        </div>
        <div className="w-px bg-gray-200 h-7 dark:bg-gray-800"></div>
        <div>
          <p
            className="mb-1 text-center text-theme-xs sm:text-sm"
            style={{ color: '#FFC107' }}
          >
            In Process
          </p>
          <p
            className="text-center text-base font-semibold sm:text-lg"
            style={{ color: '#FFC107' }}
          >
            {quotationData.inProcess}
          </p>
        </div>
      </div>
    </div>
  );
}